import { Component, DestroyRef, inject, signal, ChangeDetectionStrategy } from '@angular/core';

import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { IconComponent } from '../../../../../shared/components/icon/icon.component';
import { ToggleSwitchComponent } from '../../../../../shared/components/toggle-switch/toggle-switch.component';
import { IconType } from '../../../../../shared/services/icon.service';
import { ToastService } from '../../../../../shared/services/toast.service';
import { ErrorHandlingService } from '../../../../../shared/services/error-handling.service';
import { ConfirmationDialogService } from '../../../../../shared/services/confirmation-dialog.service';
import { OrganizationService } from '../../../../../core/services/organization.service';
import {
  SepaService,
  SepaMandate,
  SEPA_MANDATE_TEXT_VERSION,
} from '../../../../services/sepa.service';

/** Strip grouping spaces and uppercase, so `de00 0000 …` → `DE000000…`. */
function normalizeIban(value: string): string {
  return (value ?? '').replace(/\s+/g, '').toUpperCase();
}

/**
 * IBAN validator: structural pattern + ISO 7064 mod-97 checksum. Catches typos
 * client-side instead of failing server-side (or days later in the pain.008 batch).
 */
function ibanValidator(control: AbstractControl): ValidationErrors | null {
  const raw = control.value as string;
  if (!raw) return null; // `required` handles emptiness
  const iban = normalizeIban(raw);
  if (!/^[A-Z]{2}\d{2}[A-Z0-9]{11,30}$/.test(iban)) {
    return { invalidIban: true };
  }
  // Move the first 4 chars to the end, map A–Z → 10–35, then mod 97 must equal 1.
  const rearranged = iban.slice(4) + iban.slice(0, 4);
  const numeric = rearranged.replace(/[A-Z]/g, (ch) =>
    String(ch.charCodeAt(0) - 55),
  );
  let remainder = 0;
  for (const digit of numeric) {
    remainder = (remainder * 10 + Number(digit)) % 97;
  }
  return remainder === 1 ? null : { invalidIban: true };
}

/**
 * Profile sub-component: manage the user's SEPA Lastschrift bank data + mandate
 * for the currently selected organization. An IBAN/holder change is a NEW mandate
 * (revoke + re-accept), matching the backend.
 */
@Component({
  selector: 'app-sepa-account-info',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    IconComponent,
    ToggleSwitchComponent
],
  templateUrl: './sepa-account-info.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['../_collapsible-card.css', './sepa-account-info.component.css'],
})
export class SepaAccountInfoComponent {
  private fb = inject(FormBuilder);
  private destroyRef = inject(DestroyRef);
  private sepaService = inject(SepaService);
  private organizationService = inject(OrganizationService);
  private toastService = inject(ToastService);
  private errorHandlingService = inject(ErrorHandlingService);
  private confirmationDialogService = inject(ConfirmationDialogService);

  readonly IconType = IconType;
  isExpanded = signal(false);
  isSaving = signal(false);
  isLoading = signal(false);
  mandate = signal<SepaMandate | null>(null);
  editingBankData = signal(false);

  form: FormGroup = this.fb.group({
    accountHolderName: ['', [Validators.required, Validators.maxLength(70)]],
    iban: ['', [Validators.required, ibanValidator]],
    // BIC is 8 or 11 alphanumerics (SWIFT); optional, validated only when present.
    bic: ['', [Validators.pattern(/^[A-Za-z0-9]{8}([A-Za-z0-9]{3})?$/)]],
    accepted: [false, [Validators.requiredTrue]],
  });

  get selectedOrgId(): string | null {
    return this.organizationService.selectedOrganizationId();
  }

  /** Creditor name shown in the mandate authorization text (the selected club). */
  get creditorName(): string {
    return this.organizationService.getSelectedOrganization()?.name ?? 'den Verein';
  }

  toggleExpanded(): void {
    this.isExpanded.update((v) => !v);
    if (this.isExpanded()) {
      this.loadMandate();
    }
  }

  private loadMandate(): void {
    const orgId = this.selectedOrgId;
    if (!orgId) {
      this.mandate.set(null);
      return;
    }
    this.isLoading.set(true);
    this.sepaService
      .getMyMandate(orgId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (mandate) => {
          this.mandate.set(mandate);
          this.editingBankData.set(!mandate);
          this.isLoading.set(false);
        },
        error: (err) => {
          this.isLoading.set(false);
          this.toastService.error(
            this.errorHandlingService.getGeneralErrorMessage(err),
          );
        },
      });
  }

  startEdit(): void {
    this.form.reset({
      accountHolderName: '',
      iban: '',
      bic: '',
      accepted: false,
    });
    this.editingBankData.set(true);
  }

  cancelEdit(): void {
    if (this.mandate()) {
      this.editingBankData.set(false);
    } else {
      this.isExpanded.set(false);
    }
  }

  onSubmit(): void {
    const orgId = this.selectedOrgId;
    if (!orgId) {
      this.toastService.warning('Bitte zuerst einen Verein auswählen');
      return;
    }
    if (!this.form.valid || this.isSaving()) {
      this.form.markAllAsTouched();
      return;
    }
    this.isSaving.set(true);
    const value = this.form.value;
    const bic = (value.bic || '').replace(/\s+/g, '').toUpperCase();
    this.sepaService
      .createMandate({
        organizationId: orgId,
        accountHolderName: value.accountHolderName,
        iban: normalizeIban(value.iban),
        bic: bic || undefined,
        mandateTextVersion: SEPA_MANDATE_TEXT_VERSION,
        accepted: value.accepted,
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (mandate) => {
          this.isSaving.set(false);
          this.mandate.set(mandate);
          this.editingBankData.set(false);
          this.toastService.success('SEPA-Mandat gespeichert');
        },
        error: (err) => {
          this.isSaving.set(false);
          this.toastService.error(
            this.errorHandlingService.getGeneralErrorMessage(err),
          );
        },
      });
  }

  async revoke(): Promise<void> {
    const mandate = this.mandate();
    if (!mandate) return;
    const confirmed = await this.confirmationDialogService.confirm({
      title: 'SEPA-Mandat widerrufen',
      message:
        'Möchten Sie das SEPA-Lastschriftmandat wirklich widerrufen? Sie müssen anschließend ein neues Mandat erteilen, um per Lastschrift zu bezahlen.',
      confirmText: 'Widerrufen',
      cancelText: 'Abbrechen',
      confirmButtonClass: 'btn-danger',
      icon: 'warning',
    });
    if (!confirmed) return;
    this.sepaService
      .revokeMandate(mandate.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.mandate.set(null);
          this.editingBankData.set(true);
          this.toastService.success('SEPA-Mandat widerrufen');
        },
        error: (err) => {
          this.toastService.error(
            this.errorHandlingService.getGeneralErrorMessage(err),
          );
        },
      });
  }

  setAccepted(value: boolean): void {
    const control = this.form.get('accepted');
    control?.setValue(value);
    control?.markAsTouched();
  }

  hasError(field: string, error: string): boolean {
    const control = this.form.get(field);
    return !!(control?.touched && control?.hasError(error));
  }
}
